import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';

// Radar sweep animation (rotating line)
const radarSweep = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Expanding wave animation
const expandWave = keyframes`
  0% {
    transform: scale(0);
    opacity: 0.8;
  }
  50% {
    opacity: 0.5;
  }
  99% {
    transform: scale(1);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
    visibility: hidden;
  }
`;

/**
 * RadarScan Component - Visual radar sweep animation overlay (500m radius, 5 seconds)
 * @param {Object} center - { lat, lng } center of radar
 * @param {boolean} active - Whether radar is currently active
 * @param {boolean} itemsDetected - Whether items were found
 */
function RadarScan({ center, active, itemsDetected, mapRef }) {
  const [waves, setWaves] = useState([]);

  useEffect(() => {
    if (active) {
      // Create 3 expanding waves during the sweep
      const waveTimings = [0, 1666, 3333]; // Staggered over 5 seconds
      const baseTime = Date.now();
      waveTimings.forEach((delay, index) => {
        setTimeout(() => {
          setWaves(prev => [...prev, { id: `${baseTime}-${index}` }]);
          // Remove this wave after its animation completes (1.5s)
          setTimeout(() => {
            setWaves(prev => prev.filter(w => w.id !== `${baseTime}-${index}`));
          }, 1500);
        }, delay);
      });

      // Clear all waves after animation completes (safety cleanup)
      setTimeout(() => {
        setWaves([]);
      }, 5000);
    }
  }, [active]);

  if (!active || !center) {
    return null;
  }

  const map = mapRef?.getMap?.();
  if (!map) return null;

  const projection = map.getProjection();
  const bounds = map.getBounds();

  if (!projection || !bounds) return null;

  // Get map container dimensions
  const mapDiv = map.getDiv();
  const mapWidth = mapDiv.offsetWidth;
  const mapHeight = mapDiv.offsetHeight;

  // Convert lat/lng to pixel coordinates
  const latLngToPoint = (latLng) => {
    const worldCoordinate = projection.fromLatLngToPoint(latLng);
    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());

    const x = ((worldCoordinate.x - bottomLeft.x) / (topRight.x - bottomLeft.x)) * mapWidth;
    const y = ((worldCoordinate.y - topRight.y) / (bottomLeft.y - topRight.y)) * mapHeight;

    return { x, y };
  };

  const centerPoint = latLngToPoint(new window.google.maps.LatLng(center.lat, center.lng));

  return (
    <>
      {/* Overlay for sweep animation */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1000,
        }}
      >
        {/* Center dot */}
        <Box
          sx={{
            position: 'absolute',
            left: centerPoint.x,
            top: centerPoint.y,
            width: '16px',
            height: '16px',
            marginLeft: '-8px',
            marginTop: '-8px',
            borderRadius: '50%',
            backgroundColor: itemsDetected ? 'rgba(76, 175, 80, 0.9)' : 'rgba(76, 175, 80, 0.7)',
            boxShadow: itemsDetected
              ? '0 0 20px rgba(76, 175, 80, 1)'
              : '0 0 10px rgba(76, 175, 80, 0.7)',
          }}
        />

        {/* Rotating sweep line */}
        <Box
          sx={{
            position: 'absolute',
            left: centerPoint.x,
            top: centerPoint.y,
            width: '300px',
            height: '3px',
            marginLeft: '0',
            marginTop: '-1.5px',
            background: itemsDetected
              ? 'linear-gradient(90deg, rgba(76, 175, 80, 0.8) 0%, rgba(76, 175, 80, 0) 100%)'
              : 'linear-gradient(90deg, rgba(76, 175, 80, 0.6) 0%, rgba(76, 175, 80, 0) 100%)',
            transformOrigin: '0 50%',
            animation: `${radarSweep} 5s linear`,
          }}
        />

        {/* Expanding waves */}
        {waves.map((wave) => (
          <Box
            key={wave.id}
            sx={{
              position: 'absolute',
              left: centerPoint.x,
              top: centerPoint.y,
              width: '200px',
              height: '200px',
              marginLeft: '-100px',
              marginTop: '-100px',
              borderRadius: '50%',
              border: itemsDetected
                ? '3px solid rgba(76, 175, 80, 0.8)'
                : '2px solid rgba(76, 175, 80, 0.6)',
              animation: `${expandWave} 1.5s ease-out forwards`,
            }}
          />
        ))}
      </Box>
    </>
  );
}

export default RadarScan;
